import { useWatch } from 'react-hook-form';
import {
  TextInput,
  SelectInput,
  BooleanInput,
  required,
  useTranslate,
} from 'react-admin';
import { Box, Card, CardContent, Typography, Alert } from '@mui/material';
import {
  MODEL_PROVIDERS,
  MODEL_ROLES,
  PROVIDER_LABELS,
  formatParams,
  parseParamsInput,
  validateParamsJson,
} from './modelConfigFormHelpers';

// Warning shown when isDefault is toggled on (mirrors the DB one-default-per-role invariant, REF-01
// s.1.1). The form submits only this record; the backend clears the prior default transactionally
// (sub-plan 17 `ensureSingleDefault`). It never silently mutates other rows.
const DefaultWarning = () => {
  const translate = useTranslate();
  const isDefault = useWatch({ name: 'isDefault' });
  if (!isDefault) return null;
  return (
    <Alert severity="info" sx={{ mt: 1 }}>
      {translate('resources.ai-model-configs.helpers.defaultWarning', {
        _: 'Only one default model is allowed per role; saving this will replace any existing default for the selected role.',
      })}
    </Alert>
  );
};

// Shared SimpleForm body used by both Create and Edit. Editable fields map 1:1 to REF-01 s.1.1
// columns. Server-managed fields (id, createdAt, updatedAt) are never rendered, so they never enter
// the form payload. Layout uses MUI v7 Box flexbox (no Grid).
export const ModelConfigForm = () => {
  const translate = useTranslate();

  const providerChoices = MODEL_PROVIDERS.map((p) => ({ id: p, name: PROVIDER_LABELS[p] }));
  const roleChoices = MODEL_ROLES.map((r) => ({
    id: r,
    name: translate(`resources.ai-model-configs.roles.${r}`, { _: r }),
  }));

  return (
    <Box sx={{ width: '100%', maxWidth: 980, '& .MuiFormControl-root': { width: '100%' } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left column: identity + provider/model */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-model-configs.sections.identity', { _: 'Identity' })}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <SelectInput
                  source="provider"
                  label={translate('resources.ai-model-configs.fields.provider', { _: 'Provider' })}
                  choices={providerChoices}
                  validate={[required()]}
                  fullWidth
                />
                <TextInput
                  source="modelName"
                  label={translate('resources.ai-model-configs.fields.modelName', { _: 'Model Name' })}
                  validate={[required()]}
                  helperText={translate('resources.ai-model-configs.helpers.modelName', {
                    _: 'The provider model id (e.g. glm-4-plus, qwen-max).',
                  })}
                  fullWidth
                />
                <TextInput
                  source="displayName"
                  label={translate('resources.ai-model-configs.fields.displayName', {
                    _: 'Display Name',
                  })}
                  validate={[required()]}
                  helperText={translate('resources.ai-model-configs.helpers.displayName', {
                    _: 'Human label shown in the replay picker.',
                  })}
                  fullWidth
                />
                <SelectInput
                  source="role"
                  label={translate('resources.ai-model-configs.fields.role', { _: 'Role' })}
                  choices={roleChoices}
                  defaultValue="investigation"
                  validate={[required()]}
                  fullWidth
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right column: routing + flags + params */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-model-configs.sections.behaviour', { _: 'Behaviour' })}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="gatewayRoute"
                  label={translate('resources.ai-model-configs.fields.gatewayRoute', {
                    _: 'Gateway Route',
                  })}
                  helperText={translate('resources.ai-model-configs.helpers.gatewayRoute', {
                    _: 'Optional AI Gateway path/base endpoint. Leave empty for the default.',
                  })}
                  fullWidth
                />
                <BooleanInput
                  source="isActive"
                  label={translate('resources.ai-model-configs.fields.isActive', { _: 'Active' })}
                  defaultValue={true}
                  helperText={translate('resources.ai-model-configs.helpers.isActive', {
                    _: 'Inactive configs are hidden from the replay picker but still listed here.',
                  })}
                />
                <BooleanInput
                  source="isDefault"
                  label={translate('resources.ai-model-configs.fields.isDefault', { _: 'Default' })}
                  defaultValue={false}
                  helperText={translate('resources.ai-model-configs.helpers.isDefault', {
                    _: 'At most one default per role.',
                  })}
                />
                <DefaultWarning />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-model-configs.sections.params', { _: 'Parameters' })}
              </Typography>
              <TextInput
                source="params"
                label={translate('resources.ai-model-configs.fields.params', { _: 'Params (JSON)' })}
                multiline
                minRows={5}
                fullWidth
                format={formatParams}
                parse={parseParamsInput}
                validate={validateParamsJson}
                placeholder='{ "temperature": 0.2, "max_tokens": 4096 }'
                helperText={translate('resources.ai-model-configs.helpers.params', {
                  _: 'JSON object of model params (temperature, max_tokens, ...).',
                })}
                sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ModelConfigForm;
